import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { v4 as uuid } from 'uuid';

import {
  BibliographicResource,
  BibliographicResourceType,
} from '®shared/bibliographic-resource';

import { ResourceEditFormComponent } from './resource-edit-form.component';
import {
  FieldAndResourcePart,
  VisibleFieldsForPartResource,
  VisibleFieldsForResource,
} from './visible-fields';

@Component({
  selector: 'rl-resource-edit',
  templateUrl: 'resource-edit.component.html',
  styleUrls: ['./resource-edit.component.scss'],
})
export class ResourceEditComponent implements OnInit {
  @Input() resource: BibliographicResource;
  @Output() backButtonClicked: EventEmitter<void> = new EventEmitter();
  @Output() updateBibliographicResource: EventEmitter<BibliographicResource> =
    new EventEmitter();

  @ViewChild('mainResourceForm')
  mainResourceFormComponent: ResourceEditFormComponent;
  @ViewChild('partOfResourceForm')
  partOfResourceFormComponent: ResourceEditFormComponent;

  visibleFields: VisibleFieldsForResource = {
    mainResource: {
      fields: {},
    },
    partOfResource: {
      fields: {},
    },
  };

  hasPartOfResource = false;

  ngOnInit(): void {
    this.visibleFields.mainResource = this.setVisibleFields(this.resource);

    if (this.resource.part_of) {
      this.hasPartOfResource = true;
      this.visibleFields.partOfResource = this.setVisibleFields(
        this.resource.part_of,
      );
    }
  }

  private setVisibleFields(
    resource: BibliographicResource,
  ): VisibleFieldsForPartResource {
    return {
      fields: {
        authors: Boolean(resource.authors?.length),
        court: Boolean(resource.court),
        dates: Boolean(resource.dates?.length),
        doi: Boolean(resource.doi),
        edition: Boolean(resource.edition),
        editors: Boolean(resource.editors?.length),
        eissns: Boolean(resource.eissns?.length),
        format: Boolean(resource.format),
        identifier: Boolean(resource.identifier),
        optionalIsbn10s: Boolean(resource.optionalIsbn10s?.length),
        optionalIsbn13s: Boolean(resource.optionalIsbn13s?.length),
        issns: Boolean(resource.issns?.length),
        issue: Boolean(resource.issue),
        lcn: Boolean(resource.lcn),
        location: Boolean(resource.location),
        number: Boolean(resource.number),
        online_resource: Boolean(resource.online_resource),
        page_end: Boolean(resource.page_end),
        page_start: Boolean(resource.page_start),
        places_of_publication: Boolean(resource.places_of_publication?.length),
        publisher_names: Boolean(resource.publisher_names?.length),
        series: Boolean(resource.series),
        source_uri: Boolean(resource.source_uri),
        volume: Boolean(resource.volume),
        web_addresses: Boolean(resource.web_addresses?.length),
      },
    };
  }

  private getFormValues(
    obj: Record<string, string>,
    valueKey: string,
  ): string[] {
    return Object.keys(obj).map(e => obj[e][valueKey]);
  }

  addFieldToForm(fieldAndResourcePart: FieldAndResourcePart): void {
    if (fieldAndResourcePart.isPartOfResource) {
      this.visibleFields.partOfResource.fields[
        fieldAndResourcePart.field.name
      ] = true;
      this.partOfResourceFormComponent.addIterableField(
        fieldAndResourcePart.field,
      );
    } else {
      this.visibleFields.mainResource.fields[fieldAndResourcePart.field.name] =
        true;
      this.mainResourceFormComponent.addIterableField(
        fieldAndResourcePart.field,
      );
    }
  }

  addHasPartResource(): void {
    if (this.resource.part_of) {
      return;
    }

    const existingPrimaryResourceId = this.resource.id;
    const newPartOfResourceId = uuid();

    const tmpOldMainResource = BibliographicResource.create({
      ...this.resource,
      part_of: undefined,
    });

    this.resource = BibliographicResource.create({
      id: existingPrimaryResourceId,
      resource_type: BibliographicResourceType.Document,
      title: '',
      part_of: BibliographicResource.create({
        ...tmpOldMainResource,
        id: newPartOfResourceId,
      }),
    });

    this.hasPartOfResource = true;
    this.visibleFields.mainResource = this.setVisibleFields(this.resource);
    this.visibleFields.partOfResource = this.setVisibleFields(
      this.resource.part_of,
    );
  }

  removeHasPartResource(): void {
    if (!this.resource.part_of) {
      return;
    }

    const existingPartOfResourceId = this.resource.id;

    this.resource = BibliographicResource.create({
      ...this.resource.part_of,
      id: existingPartOfResourceId,
    });

    this.hasPartOfResource = false;
    this.visibleFields.mainResource = this.setVisibleFields(this.resource);
    this.visibleFields.partOfResource.fields = {};
  }

  addPartOfResource(): void {
    if (this.resource.part_of) {
      return;
    }

    this.resource = BibliographicResource.create({
      ...this.resource,
      part_of: BibliographicResource.create({
        id: uuid(),
        resource_type: BibliographicResourceType.Document,
      }),
    });

    this.hasPartOfResource = true;
    this.visibleFields.partOfResource = this.setVisibleFields(
      this.resource.part_of,
    );
  }

  removePartOfResource(): void {
    if (!this.resource.part_of) {
      return;
    }

    this.resource = BibliographicResource.create({
      ...this.resource,
      part_of: undefined,
    });

    this.hasPartOfResource = false;
    this.visibleFields.mainResource = this.setVisibleFields(this.resource);
    this.visibleFields.partOfResource.fields = {};
  }

  back(): void {
    this.backButtonClicked.emit();
  }

  mapFormValuesToResource(): BibliographicResource {
    const mainResourceForm = this.mainResourceFormComponent.form;
    const mainResourceValuesRaw = mainResourceForm.value;

    if (!mainResourceForm.valid) {
      return;
    }

    const mainResourceValues = {
      ...mainResourceValuesRaw,
      optionalIsbn10s: this.getFormValues(
        mainResourceValuesRaw.optionalIsbn10s,
        'isbn10',
      ),
      optionalIsbn13s: this.getFormValues(
        mainResourceValuesRaw.optionalIsbn13s,
        'isbn13',
      ),
      dates: this.getFormValues(mainResourceValuesRaw.dates, 'date'),
      publisher_names: this.getFormValues(
        mainResourceValuesRaw.publisher_names,
        'publisherName',
      ),
      places_of_publication: this.getFormValues(
        mainResourceValuesRaw.places_of_publication,
        'placeOfPublication',
      ),
      issns: this.getFormValues(mainResourceValuesRaw.issns, 'issn'),
      eissns: this.getFormValues(mainResourceValuesRaw.eissns, 'eissn'),
      web_addresses: this.getFormValues(
        mainResourceValuesRaw.web_addresses,
        'webAddress',
      ),
    };

    const resource = BibliographicResource.create({
      ...this.resource,
      ...mainResourceValues,
    });

    if (this.resource.part_of) {
      const partOfResourceForm = this.partOfResourceFormComponent.form;
      const partResourceValuesRaw = partOfResourceForm.value;

      if (!partOfResourceForm.valid) {
        return;
      }

      const partResourceValues = {
        ...partResourceValuesRaw,
        optionalIsbn10s: this.getFormValues(
          partResourceValuesRaw.optionalIsbn10s,
          'isbn10',
        ),
        optionalIsbn13s: this.getFormValues(
          partResourceValuesRaw.optionalIsbn13s,
          'isbn13',
        ),
        dates: this.getFormValues(partResourceValuesRaw.dates, 'date'),
        publisher_names: this.getFormValues(
          partResourceValuesRaw.publisher_names,
          'publisherName',
        ),
        places_of_publication: this.getFormValues(
          partResourceValuesRaw.places_of_publication,
          'placeOfPublication',
        ),
        issns: this.getFormValues(partResourceValuesRaw.issns, 'issn'),
        eissns: this.getFormValues(partResourceValuesRaw.eissns, 'eissn'),
        web_addresses: this.getFormValues(
          partResourceValuesRaw.web_addresses,
          'webAddress',
        ),
      };

      resource.part_of = BibliographicResource.create({
        ...this.resource.part_of,
        ...partResourceValues,
      });
    }

    return resource;
  }

  updateResource(): void {
    this.updateBibliographicResource.emit(this.mapFormValuesToResource());
  }
}
